import { useQuery } from '@tanstack/react-query';
import type { PickedReceiver } from '@/components/patient/ReferralSorPicker';
import { supabase } from '@/lib/supabase';
import { EPIKRISE_KINDS, type EpikriseKind } from '@/lib/referral/epikrise';
import { receiverIdentifierPair } from '@/lib/referral/xref01Validation';

export const SECTION =
  'overflow-hidden rounded-lg border border-ds-line bg-ds-card shadow-card';
export const HEADING = 'text-xs font-medium uppercase tracking-wide text-ds-muted';
export const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal';
export const SUB = 'space-y-2 rounded-md border border-ds-line p-3';

export const CHIP: Record<'red' | 'yellow' | 'green' | 'white', string> = {
  red: 'bg-ds-red-tint text-ds-red-ink',
  yellow: 'bg-ds-amber-tint text-ds-amber-ink',
  green: 'bg-ds-green-tint text-ds-green-ink',
  white: 'border border-ds-field bg-ds-card text-ds-text',
};

export type EpikriseOutboxRow = {
  id: string;
  kind: EpikriseKind;
  exchange_id: string;
  envelope_identifier: string;
  receiver_organisation_name: string | null;
  receiver_ean: string;
  status: string;
  created_at: string;
  letter_status_code: 'nytbrev' | 'rettetbrev' | null;
  corrects_id: string | null;
  copy_of_id: string | null;
  /** XBIN01 bilag built with the epikrise (DIS05-S 2.5/4.30/4.31), cited by Reference/BIN. */
  companions: Array<{ exchangeId: string; fileName: string }>;
};

function readCompanions(raw: unknown): Array<{ exchangeId: string; fileName: string }> {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((c) =>
    c && typeof c === 'object' && typeof (c as { exchangeId?: unknown }).exchangeId === 'string'
      ? [{ exchangeId: (c as { exchangeId: string }).exchangeId, fileName: String((c as { fileName?: unknown }).fileName ?? '') }]
      : [],
  );
}

export type EpikriseDraftRow = {
  id: string;
  kind: EpikriseKind;
  form: unknown;
  status: 'draft' | 'sent';
  corrects_outbox_id: string | null;
  sent_outbox_id: string | null;
  updated_at: string;
};

export type EpikriseCheck = { id: string; pass: boolean; detail: string };
export type EpikriseResult = {
  id: string;
  outboxId: string;
  envelopeIdentifier: string;
  kind: EpikriseKind;
  checks: EpikriseCheck[];
};
export type EpikriseCopyRequest = {
  action: 'epikrise-copy';
  clinicId: string;
  originalOutboxId: string;
  receiver: { ean: string; organisationName?: string };
};

export function filled(entries: Record<string, string | null | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  }
  return out;
}

/** A SOR pick as the party Communicate needs; null when the identifier pair is incomplete. */
export interface EpikrisePartyBody {
  ean: string;
  identifier: string;
  identifierCode: string;
  organisationName?: string;
  streetName?: string;
  postCodeIdentifier?: string;
  districtName?: string;
}

export function pickedParty(picked: PickedReceiver): EpikrisePartyBody | null {
  const pair = receiverIdentifierPair({
    registerCode: picked.organisation.identifierCode,
    chosenCode: picked.chosenIdentifierCode,
    identifier: picked.identifier,
  });
  if (!pair.ok) return null;
  return {
    ean: picked.organisation.ean,
    identifier: pair.identifier.trim(),
    identifierCode: pair.identifierCode,
    ...filled({
      organisationName: picked.organisation.name,
      streetName: picked.organisation.address.street,
      postCodeIdentifier: picked.organisation.address.postCode,
      districtName: picked.organisation.address.district,
    }),
  };
}

export function useEpikriseOutbox(referralId: string) {
  return useQuery({
    queryKey: ['referral-epikriser', referralId],
    queryFn: async (): Promise<EpikriseOutboxRow[]> => {
      const { data, error } = await supabase
        .from('clinic_communicate_outbound_referrals')
        .select(
          'id, kind, exchange_id, envelope_identifier, receiver_organisation_name, receiver_ean, status, created_at, letter_status_code, corrects_id, copy_of_id, companions',
        )
        .eq('source_referral_id', referralId)
        .in('kind', [...EPIKRISE_KINDS])
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((row) => ({ ...row, companions: readCompanions(row.companions) })) as EpikriseOutboxRow[];
    },
  });
}

export function useEpikriseDrafts(referralId: string) {
  return useQuery({
    queryKey: ['referral-epikrise-drafts', referralId],
    queryFn: async (): Promise<EpikriseDraftRow[]> => {
      const { data, error } = await supabase
        .from('clinic_epikrise_drafts')
        .select('id, kind, form, status, corrects_outbox_id, sent_outbox_id, updated_at')
        .eq('referral_id', referralId)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as EpikriseDraftRow[];
    },
  });
}
